import React, { useContext, useEffect } from 'react'
import classNames from 'classnames'
import DesktopNavItem from './DesktopNavItem'
import useWindowSize from '@toasttab/shared/src/hooks/useWindowSize'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import { I18nextContext } from '@toasttab/shared/src/context/i18nextContext'
import { useNavContext } from '@toasttab/shared/src/components/organisms/Navigation/Contentful/useNavContext'
import { localizeLink } from '@toasttab/shared/src/utils/linkUtils'
import { resolvePrefix } from '@toasttab/shared/src/utils/assetUtils'
import { NavProps } from '@toasttab/shared/src/components/organisms/Navigation/Contentful/ContentfulNavigation.d'

const DesktopNav = ({ menus }: NavProps) => {
  const localizationContext = useContext(I18nextContext)
  const {
    desktopNavHeight,
    isKeyboardNavigation,
    isOverlayOpen,
    selectedParentMenu,
    setDesktopNavHeight,
    setIsOverlayOpen,
    setSelectedParentMenu
  } = useNavContext()
  const windowSize = useWindowSize()
  const pageContext = usePageContext()
  const { headerHeight } = pageContext
  const packageName = pageContext?.packageName || ''

  useEffect(() => {
    if (!selectedParentMenu) {
      setDesktopNavHeight(0)
      setIsOverlayOpen(false)
    }
  }, [selectedParentMenu, setDesktopNavHeight, setIsOverlayOpen])

  const handleCloseNav = () => {
    if (!isKeyboardNavigation) {
      setSelectedParentMenu(null)
      setDesktopNavHeight(0)
      setIsOverlayOpen(false)
    }
  }

  const overlayCss = classNames('overlay opacity-0 fixed transition-opacity', {
    // using visibility: hidden; over display: none; to allow the opacity transition to function
    invisible: !isOverlayOpen || windowSize.width < 1024,
    [`left-0 bg-black opacity-30 w-screen h-screen`]:
      isOverlayOpen && windowSize.width >= 1024
  })

  return (
    <nav
      aria-hidden={!(windowSize.width >= 1024)}
      aria-label='Toast Desktop Navigation'
      className='h-full flex items-center'
      data-testid='DesktopNav'
    >
      <ul
        aria-label='Toast Main Navigation'
        className='col-span-12 flex flex-nowrap justify-start h-full items-center'
        role='menubar'
      >
        <li
          className='logo inline-block w-[74px] lg:w-[107px] mr-3 lg:-mt-1'
          key='desktop-parent-link-home'
          role='none'
        >
          <a
            href={
              packageName === 'support'
                ? 'https://pos.toasttab.com'
                : localizeLink(
                    '/',
                    localizationContext.language,
                    localizationContext
                  )
            }
          >
            <img
              alt='Toast Logo'
              src={resolvePrefix('/svg/img-toast-logo.svg')}
            />
          </a>
        </li>
        {menus?.map((menu: any) => (
          <DesktopNavItem
            handleCloseNav={handleCloseNav}
            key={menu.title}
            {...menu}
          />
        ))}
        <div
          className='fixed left-0 bg-white w-full rounded-b-xl drop-shadow-lg transition-all z-10'
          data-testid='DesktopNav-dropdownWrapper'
          onPointerLeave={handleCloseNav}
          style={{ height: `${desktopNavHeight}px`, top: `${headerHeight}px` }}
        />
        <div
          className={overlayCss}
          data-testid='DesktopNav-overlay'
          onMouseEnter={handleCloseNav}
          style={{ top: `${headerHeight}px` }}
        />
      </ul>
    </nav>
  )
}

export default DesktopNav
